import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { InputNew } from '@UI/InputNew'
import { classApi, massActionsApi } from '@/api'
import { Alert, DatePicker, Select, Tooltip } from 'antd'
import { useAddAlert } from '@/hooks/useAddAlert'
import { CheckBoxLabel } from '@UI/CheckBox'
import classes from './purchase.module.css'
import type { Dayjs } from 'dayjs'
import { PurchaseHandle, PurchaseHandleSaveFnParams } from '.'
import { useSelector } from 'react-redux'

interface SetClassModalBodyProps {
  excludedClassIds: number[]
}

type DateStateType = {
  dateString: string | string[]
  date: Dayjs | null
} | null

const defaultManualPaymentTypes = ['Оплата наличными', 'Оплата по счету']

const parseManualPaymentTypes = (value?: string) => {
  if (!value) return defaultManualPaymentTypes
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return defaultManualPaymentTypes
    const values = parsed
      .map((item) => (typeof item === 'string' ? item : item?.name))
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
    return [...new Set(values)]
  } catch (e) {
    return defaultManualPaymentTypes
  }
}

export type ICustomClass = Pick<
  IClass,
  | 'id'
  | 'title'
  | 'amount'
  | 'one_pay'
  | 'auto_access_duration'
  | 'auto_access_unit'
  | 'folder_ids'
  | 'created_at'
  | 'updated_at'
>

const SetClassModalBody = forwardRef<PurchaseHandle, SetClassModalBodyProps>(
  ({ excludedClassIds }, ref) => {
    const addAlert = useAddAlert()
    const settings = useSelector((store: any) => store.settings)
    const isUniversalBalance = settings?.isUniversalBalance === '1'

    const [classesList, setClassesList] = useState<ICustomClass[]>([])
    const [selectedClass, setSelectedClass] = useState<ICustomClass | null>(
      null,
    )
    const [paymentAmount, setPaymentAmount] = useState(0)
    const [manualPaymentType, setManualPaymentType] = useState<string | null>(null)
    const [isNewPaymentForClass, setNewPaymentForClass] = useState(false)
    const isManualPaymentTypeSelectionEnabled = useSelector(
      (store: any) => store.settings.isManualPaymentTypeSelectionEnabled === '1',
    )
    const manualPaymentTypes = parseManualPaymentTypes(
      useSelector((store: any) => store.settings.custom.manualPaymentTypes),
    )

    useEffect(() => {
      classApi.index().then(({ data }) => {
        const newClasses = (data.data ?? []) as ICustomClass[]
        setClassesList(
          newClasses.filter((item) => {
            return !excludedClassIds.includes(item.id)
          }),
        )
      })
    }, [])

    useEffect(() => {
      setPaymentAmount(selectedClass?.amount ?? 0)
      setNewPaymentForClass(false)
    }, [selectedClass])

    const [paidFromDate, setPaidFromDate] = useState<DateStateType>(null)
    const [paidBeforeDate, setPaidBeforeDate] = useState<DateStateType>(null)

    const onChangePaidFrom = (
      date: Dayjs | null,
      dateString: string | string[],
    ) => {
      setPaidFromDate({
        dateString: dateString,
        date: date,
      })
    }

    const onChangePaidBeforeDate = (
      date: Dayjs | null,
      dateString: string | string[],
    ) => {
      setPaidBeforeDate({
        dateString: dateString,
        date: date,
      })
    }

    const filterSort = useCallback((optionA: any, optionB: any) => {
      return (optionA?.label ?? '')
        .toLowerCase()
        .localeCompare((optionB?.label ?? '').toLowerCase())
    }, [])

    useImperativeHandle(ref, () => ({
      handleSave,
    }))

    const handleSave = async (
      props?: PurchaseHandleSaveFnParams,
    ): Promise<'success' | 'error'> => {
      if (!selectedClass?.id) {
        addAlert({
          title: 'Ошибка',
          text: 'Нужно выбрать класс/траекторию!',
          type: 'error',
        })
        return 'error'
      }
      if (
        selectedClass?.one_pay &&
        (!paidFromDate?.date || !paidBeforeDate?.date)
      ) {
        addAlert({
          title: 'Ошибка',
          text: 'Нужно выбрать дату начала и конца для класса/траектории!',
          type: 'error',
        })
        return 'error'
      }

      const data = new FormData()
      data.append(
        'users',
        JSON.stringify((props?.users || []).map((user) => user.id)),
      )
      data.append('class_id', String(selectedClass?.id))

      if (selectedClass?.one_pay) {
        data.append('paid_from', String(paidFromDate?.dateString))
        data.append('paid_before', String(paidBeforeDate?.dateString))
        data.append('new_payment', String(isNewPaymentForClass))
        data.append('payment_amount', String(paymentAmount))
        if (manualPaymentType) {
          data.append('manual_payment_type', manualPaymentType)
        }
      }

      if (props?.paymentFrom !== undefined) {
        data.append(
          'balance_action',
          String(props.paymentFrom === 'crm' ? 7 : ''),
        )
      }

      if (props?.othersParams !== undefined) {
        Object.entries(props.othersParams).forEach(([key, value]) => {
          data.append(key, String(value))
        })
      }

      const handleResponse = (res: any): 'success' | 'error' => {
        if (res.data.status !== 'success') {
          addAlert({
            title: 'Ошибка',
            text: 'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
            type: 'error',
          })
          return 'error'
        } else {
          addAlert({
            title: 'Добавление прошло успешно!',
          })
          return 'success'
        }
      }

      try {
        const res = await massActionsApi.addClassesDirections(data)
        return handleResponse(res)
      } catch (error) {
        addAlert({
          title: 'Ошибка',
          text: 'Что-то пошло не так. Пожалуйста, попробуйте ещё раз.',
          type: 'error',
        })
        return 'error'
      }
    }

    return (
      <div className={classes.formWrapper}>
        <div className={classes.contentBodyWrapper}>
          <div className={classes.inputWithLabel}>
            <div className={classes.inputLabel}>Класс/траектория</div>
            <Select
              showSearch
              className={classes.select}
              placeholder="Выберите класс/траекторию"
              optionFilterProp="label"
              value={selectedClass?.id}
              onChange={(e) =>
                setSelectedClass(
                  classesList.find((item) => {
                    return item?.id === e
                  }) || null,
                )
              }
              filterSort={filterSort}
              options={classesList?.map(({ title: label, id: value }) => ({
                label,
                value,
              }))}
            />
          </div>
          {selectedClass &&
            (selectedClass?.one_pay ? (
              <>
                <div className={classes.textDescription}>
                  Базовая стоимость, мес: {selectedClass.amount} ₽
                </div>
                <Tooltip
                  title={
                    'Сумма, которую заплатили учащиеся за доступ к классу/траектории, одинаковая для всех!'
                  }
                >
                  {/* @ts-ignore */}
                  <InputNew
                    className={classes.input}
                    title="Сумма оплаты"
                    value={paymentAmount ?? 0}
                    type="number"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (!isUniversalBalance) {
                        setPaymentAmount(Number(e.target.value))
                      }
                    }}
                    placeholder={0}
                  />
                </Tooltip>
                {isManualPaymentTypeSelectionEnabled && (
                  <div className={classes.inputWithLabel}>
                    <div className={classes.inputLabel}>Тип ручного платежа</div>
                    <Select
                      showSearch
                      allowClear
                      className={classes.select}
                      placeholder="Не выбрано"
                      optionFilterProp="label"
                      value={manualPaymentType}
                      onChange={setManualPaymentType}
                      filterSort={filterSort}
                      options={manualPaymentTypes.map((typeName) => ({
                        label: typeName,
                        value: typeName,
                      }))}
                    />
                  </div>
                )}
                <CheckBoxLabel
                  value={isNewPaymentForClass}
                  onClick={() => {
                    setNewPaymentForClass((prev) => !prev)
                  }}
                  label="Это новая оплата"
                  style={{ justifyContent: 'flex-start' }}
                />
                <div className={classes.inputWithLabel}>
                  <div className={classes.inputLabel}>Период</div>
                  <div className={classes.dateSelectionRow}>
                    <DatePicker
                      value={paidFromDate?.date}
                      onChange={onChangePaidFrom}
                      format={'YYYY.MM.DD'}
                      placeholder="С какого числа оплачен класс"
                      className={classes.datePicker}
                    />
                    <DatePicker
                      value={paidBeforeDate?.date}
                      onChange={onChangePaidBeforeDate}
                      format={'YYYY.MM.DD'}
                      placeholder="До какого числа оплачен класс"
                      className={classes.datePicker}
                    />
                  </div>
                </div>
              </>
            ) : (
              <Alert
                className={classes.alert}
                message="Оплата за данный класс производится отдельно по каждому направлению"
                type="info"
              />
            ))}
        </div>
      </div>
    )
  },
)
SetClassModalBody.displayName = 'SetClassModalBody'

export default SetClassModalBody
